/**
 * Hook to check if the current user has admin role
 */

import { useState, useEffect } from 'react';
// Fixed imports to use correct paths
import { useUser } from '@foundation/contextapi/UserContext';
import { supabase } from '@foundation/utils/supabaseClient';

type SuperAdminStatus = {
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
};

export const useSuperAdmin = (): SuperAdminStatus => {
  const { user } = useUser();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSuperAdminStatus = async () => {
      if (!user?.id) {
        setIsSuperAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const { data, error: queryError } = await supabase
          .schema('altzero')
          .from('base_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (queryError) {
          console.error('Error checking admin status:', queryError);
          setError('Failed to verify admin status');
          setIsSuperAdmin(false);
          return;
        }

        setIsSuperAdmin(data?.role === 'admin');
      } catch (err) {
        console.error('Error in useSuperAdmin:', err);
        setError('Failed to verify admin status');
        setIsSuperAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSuperAdminStatus();
  }, [user?.id]);

  return { isSuperAdmin, isLoading, error };
};
